import { FormGroup } from '@angular/forms';
import { isBefore } from 'date-fns';
import { isEqual } from 'lodash-es';
import { addRemoveError, getControl, getFormArray } from '@shared/helpers/form.helper';
import { isNilOrEmpty, toBoolean } from '@shared/helpers/utils.helper';
import { REQUIRED_STATUS, VALUE_CANNOT_BE_SELECTED } from '@shared/constants/shared.constants';
import { FormGroupRules } from '@shared/models/interfaces/form-group-rules.model';
import { formPaths } from '@shared/constants/form-paths.const';
import { getDatiIdentificativiControl, getDatiIdentificativiGroup } from './helpers/dati-identificativi-form-validators.helper';

export const datiIdentificativiRevisioneAttivitaRules: FormGroupRules = {
    my_LuogoRilevazione: (form: FormGroup) => {
        const my_LuogoRilevazioneControl = getDatiIdentificativiControl(form, 'my_LuogoRilevazione');
        const errors = my_LuogoRilevazioneControl.errors ? my_LuogoRilevazioneControl.errors : {};

        addRemoveError(isNilOrEmpty(my_LuogoRilevazioneControl?.value), errors, 'my_LuogoRilevazione', {
            status: 'Attenzione: indicare un luogo',
            message: 'Indicare il luogo dove principalmente si effettua la revisione.',
        });

        addRemoveError(my_LuogoRilevazioneControl?.value === '---', errors, 'my_LuogoRilevazione', {
            status: 'Attenzione: selezionare un valore',
        });

        my_LuogoRilevazioneControl.setErrors(isEqual(errors, {}) ? null : errors);
    },

    my_SpecificaLuogoRilevazione: (form: FormGroup) => {
        const datiIdentificativiGroup = getDatiIdentificativiGroup(form);
        const my_SpecificaLuogoRilevazioneControl = getControl(datiIdentificativiGroup, 'my_SpecificaLuogoRilevazione');
        const my_LuogoRilevazione: string = getControl(datiIdentificativiGroup, 'my_LuogoRilevazione')?.value;
        const errors = my_SpecificaLuogoRilevazioneControl.errors ? my_SpecificaLuogoRilevazioneControl.errors : {};

        addRemoveError(my_LuogoRilevazione === 'Altro luogo ...' && isNilOrEmpty(my_SpecificaLuogoRilevazioneControl?.value), errors, 'my_SpecificaLuogoRilevazione', {
            status: REQUIRED_STATUS,
        });

        my_SpecificaLuogoRilevazioneControl.setErrors(isEqual(errors, {}) ? null : errors);
    },

    my_DataRevisioneDal: (form: FormGroup) => {
        const datiIdentificativiGroup = getDatiIdentificativiGroup(form);
        const my_DataRevisioneDalControl = getControl(datiIdentificativiGroup, 'my_DataRevisioneDal');
        const my_DataCompilaz: string = getControl(form, `${formPaths.conclusioni}.my_DataCompilaz`)?.value;
        const my_Data_Lettera_Incarico: string = getControl(datiIdentificativiGroup, 'data_lettera_Incarico')?.value;
        const my_Anno1: string = getControl(datiIdentificativiGroup, 'my_Anno1')?.value;
        const my_Anno2: string = getControl(datiIdentificativiGroup, 'my_Anno2')?.value;
        const my_TipoIncaricoEffettivo: string = getControl(datiIdentificativiGroup, 'my_TipoIncaricoEffettivo')?.value;
        const errors = my_DataRevisioneDalControl.errors ? my_DataRevisioneDalControl.errors : {};

        const dataRevisioneDal = new Date(my_DataRevisioneDalControl.value);
        const dataRevisioneAl = new Date(my_DataCompilaz);
        const dataLetteraIncarico = new Date(my_Data_Lettera_Incarico);

        const resultDifferenceInTime = dataRevisioneAl.getTime() - dataRevisioneDal.getTime();
        const resultDifferenceInDays = resultDifferenceInTime / (1000 * 3600 * 24);

        const resultIsBefore = isBefore(dataRevisioneDal, new Date('2020-06-24'));
        const dataCompilazBeforeDataLettera = isBefore(dataRevisioneDal, dataLetteraIncarico);
        const resultIsEqual = isEqual(dataRevisioneDal, new Date('2020-06-24'));

        const annoDataRevisioneDal = Number(dataRevisioneDal.getFullYear());

        addRemoveError(isNilOrEmpty(my_DataRevisioneDalControl?.value), errors, 'my_DataRevisioneDal', {
            status: REQUIRED_STATUS,
        });

        addRemoveError(resultDifferenceInDays > 90, errors, 'my_DataRevisioneDal', {
            status: 'Attenzione: durata della revisione non conforme',
            message: 'La revisione non può durare più di 90 giorni.',
        });

        addRemoveError(resultIsBefore || resultIsEqual, errors, 'my_DataRevisioneDal', {
            status: 'Attenzione: data non conforme al presente modello di verbale',
            message: 'Il presente modello di verbale non è valido alla data inizio revisione',
        });

        addRemoveError(resultDifferenceInDays < 0, errors, 'my_DataRevisioneDal', {
            status: 'Attenzione: periodo di revisione non coerente',
            message: 'Periodo di revisione non coerente',
        });

        addRemoveError(annoDataRevisioneDal?.toString(10) != my_Anno1 && annoDataRevisioneDal?.toString(10) != my_Anno2, errors, 'my_DataRevisioneDal', {
            status: 'Attenzione: data inizio revisione fuori biennio',
            message: 'Data inizio revisione fuori biennio',
        });

        addRemoveError(my_TipoIncaricoEffettivo === '1' && annoDataRevisioneDal?.toString(10) !== my_Anno1, errors, 'my_DataRevisioneDal_annuale1', {
            status: 'Revisione annuale: anno non coerente con Anno 1',
        });

        addRemoveError(my_TipoIncaricoEffettivo === '2' && annoDataRevisioneDal?.toString(10) !== my_Anno2, errors, 'my_DataRevisioneDal_annuale2', {
            status: 'Revisione annuale: anno non coerente con Anno 2',
        });

        addRemoveError(dataCompilazBeforeDataLettera, errors, 'my_DataRevisioneDal', {
            status: 'Inizio revisione non coerente',
            message: 'Inizio revisione precedente alla data di assegnazione',
        });

        my_DataRevisioneDalControl.setErrors(isEqual(errors, {}) ? null : errors);
    },

    my_TipoIncaricoEffettivo: (form: FormGroup) => {
        const my_TipoIncaricoEffettivoControl = getDatiIdentificativiControl(form, 'my_TipoIncaricoEffettivo');
        const errors = my_TipoIncaricoEffettivoControl.errors ?? {};

        addRemoveError(my_TipoIncaricoEffettivoControl.value === '0', errors, 'my_TipoIncaricoEffettivo', {
            status: VALUE_CANNOT_BE_SELECTED,
        });

        my_TipoIncaricoEffettivoControl.setErrors(isEqual(errors, {}) ? null : errors);
    },

    my_CkAderenteA: (form: FormGroup) => {
        const my_ElAderenteA = getFormArray(form, `${formPaths.datiIdentificativi}.my_AderentiA.my_ElAderentiA`);
        my_ElAderenteA.controls.map((e: FormGroup) => {
            const my_CkAderenteA = getControl(e, 'my_SezVisibAderenteA.my_CkAderenteA');
            const my_aderenteDal = getControl(e, 'my_SezVisibAderenteA.my_aderenteDal');
            const errors = e.errors ? e.errors : {};
            const showError = !toBoolean(my_CkAderenteA.value) && !isNilOrEmpty(my_aderenteDal?.value);

            addRemoveError(showError, errors, 'my_CkAderenteA', {
                status: REQUIRED_STATUS,
            });

            e.setErrors(isEqual(errors, {}) ? null : errors);

            const aderenteDalErrors = my_aderenteDal.errors ?? {};
            addRemoveError(isNilOrEmpty(my_aderenteDal?.value) && my_CkAderenteA?.value === '1', aderenteDalErrors, 'my_aderenteDal', {
                status: REQUIRED_STATUS,
            });
            my_aderenteDal.setErrors(isEqual(aderenteDalErrors, {}) ? null : aderenteDalErrors);
        });
    },

    my_FileStatuto: (form: FormGroup) => {
        const datiIdentificativiGroup = getDatiIdentificativiGroup(form);
        const my_FileStatutoControl = getControl(datiIdentificativiGroup, 'my_FileStatuto');
        const my_DenominazEnte: string = getControl(datiIdentificativiGroup, 'my_DenominazEnte')?.value;
        const my_DenominazEntePrecaricata: string = getControl(datiIdentificativiGroup, 'my_DenominazEntePrecaricata')?.value;
        const errors = my_FileStatutoControl.errors ?? {};

        addRemoveError(isNilOrEmpty(my_FileStatutoControl.value) && my_DenominazEnte !== my_DenominazEntePrecaricata, errors, 'my_FileStatuto', {
            status: 'Allegare obbligatoriamente lo statuto',
        });

        my_FileStatutoControl.setErrors(isEqual(errors, {}) ? null : errors);
    },

    my_AltraPEC: (form: FormGroup) => {
        const my_AltraPECControl = getDatiIdentificativiControl(form, 'my_AltraPEC');
        const errors = my_AltraPECControl.errors ?? {};
        const value: string = my_AltraPECControl?.value ?? '';

        addRemoveError(isNilOrEmpty(value), errors, 'my_AltraPEC_required', {
            status: REQUIRED_STATUS,
            message: "Campo obbligatorio oppure inserire 'non presente' se la cooperativa non ha comunicato la Pec al Registro imprese.",
        });

        if (value === 'non presente' && errors['pattern']) {
            delete errors['pattern'];
        }

        my_AltraPECControl.setErrors(isEqual(errors, {}) ? null : errors);
    },

    my_SoggDispostoRev: (form: FormGroup) => {
        const datiIdentificativiGroup = getDatiIdentificativiGroup(form);
        const my_SoggDispostoRevControl = getControl(datiIdentificativiGroup, 'my_SoggDispostoRev');
        const my_DataUltRev: string = getControl(datiIdentificativiGroup, 'my_DataUltRev')?.value;
        const errors = my_SoggDispostoRevControl.errors ? my_SoggDispostoRevControl.errors : {};
        const showError = !isNilOrEmpty(my_DataUltRev) && isNilOrEmpty(my_SoggDispostoRevControl?.value);

        addRemoveError(showError, errors, 'my_SoggDispostoRev', {
            status: REQUIRED_STATUS,
        });

        my_SoggDispostoRevControl.setErrors(isEqual(errors, {}) ? null : errors);
    },
};
